import {IAvailableUnit} from "../interfaces/IAvailableUnit";
import {create} from "zustand/react";
import {ApiCall} from "../util/ApiCall";
import {IObject} from "../interfaces/IObject";
import {IRoomsData} from "../interfaces/IRoomsData";
import {IRoomData} from "../interfaces/IRoomData";
import {ICompany} from "../interfaces/ICompany";

export interface UnitsState {
  loading: boolean
  company: ICompany
  units: IAvailableUnit[]
  allUnits: IAvailableUnit[]
  objects: IObject[]
  allObjects: IObject[]
  load: () => void
  filter: (years: string[]) => void
}

export const useUnitsState = create<UnitsState>((set) => ({
  loading: false,
  company: {
    name: "",
    slug: "",
    currency: "",
    timezone: "",
    locale: "",
  },
  units: [],
  allUnits: [],
  objects: [],
  allObjects: [],
  currentObject: null,
  load: async () => {
    set(() => ({loading: true}))
    const units = await ApiCall.get<IAvailableUnit[]>("/storefront/units")
    const data = await ApiCall.get<{ company: any, objects: IObject[] }>("/storefront/objects")
    const objects = data.objects
      .map(o => {
        o.room_data = makeRoomData(o, units)
        return o
      })
      .filter(o => {
        const r = o.room_data
        return r.r1.count > 0 || r.r2.count > 0 || r.r3.count > 0 || r.r4.count > 0
      })
    set(() => ({
      loading: false,
      company: data.company,
      allUnits: units,
      units,
      objects,
      allObjects: objects
    }))
  },
  filter: (years: string[]) => set((state) => {
    const filteredUnits = state.allUnits.filter(u => {
      // return all if filter is empty or these which appear in filter horizon
      return years.length === 0 || years.indexOf(u.ready_horizon) >= 0
    })
    const filteredObjects = state.allObjects.filter(o => {
      const fu = filteredUnits.filter(u => {
        return u.object_id === o.id
      })
      return fu.length > 0;
    })
    return {units: filteredUnits, objects: filteredObjects}
  }),
}))

function makeRoomData(object: IObject, units: IAvailableUnit[]) {
  const objectUnits = units.filter(u => {
    return Number(u.object_id) === Number(object.id) && u.unit_type_category === 'apartments'
  })
  const r: IRoomData = {exist: false, count: 0, minPrice: 0, minArea: 0, rooms: 0}
  const roomsData = {"r1": r, "r2": r, "r3": r, "r4": r} as IRoomsData
  objectUnits.forEach(u => {
    const ri = "r" + u.rooms_grouped.toString()
    roomsData[ri] = {
      rooms: Number(u.rooms_grouped),
      minArea: Number(u.min_area_total),
      minPrice: u.min_price,
      exist: true,
      count: Number(u.common_units_count)
    }
  })
  return roomsData
}

export function useUnitMinPrice() {
  const units = useUnitsState()
  return units.units.reduce<number>((min: number, u: IAvailableUnit) => {
    return Number(u.min_cost) > 0 ? Math.min(min, u.min_cost) : min;
  }, Infinity)
}

//https://www.reddit.com/r/reactjs/comments/1aje0j5/how_to_create_a_computed_field_in_zustand/